import type {ReactRef} from 'src/types/react';

import * as React from 'react';

import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';

/**
 * gives the ability to respond to infinitely scroll.
 *
 * @returns [containerRef, loaderRef, handleMovement]
 *
 * any component using this hook MUST provide a function that iteratively loads
 * data and a `done` boolean to indicate when there is no more data to load.
 *
 * the component must also assign `containerRef` to its scrolling container as
 * well as `loaderRef` to an element that sits at the end of its data list.
 * when this element scrolls into view, more data will be loaded. to respond to
 * scroll events, the `handleMovement` handler must be assigned to the
 * `onScroll` event of the container.
 */

export default function useScrollLoader({
  loading,
  done,
  onLoadMore,
}: {
  onLoadMore: () => unknown;
  loading: boolean;
  done: boolean;
}): [ReactRef<HTMLElement>, ReactRef<HTMLElement>, () => void] {
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const containerRef = React.useRef<HTMLElement | null | undefined>();
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const loaderRef = React.useRef<HTMLElement | null | undefined>();

  const handleMovement = React.useCallback(
    throttle(() => {
      const container = containerRef.current;
      const loader = loaderRef.current;
      if (loading || done || !container || !loader) {
        return;
      }

      // NOTE (gab): do this part separately because it requires measuring
      // the DOM, which is more performance sensitive
      const {bottom: containerBottom, height: containerHeight} =
        container.getBoundingClientRect();
      const {top: loadingTop} = loader.getBoundingClientRect();
      const fetchLine = containerBottom + containerHeight;
      // Only fetch if the loading component is visible or one "page" away
      if (loadingTop < fetchLine) {
        onLoadMore();
      }
    }, 500),
    [loading, done, onLoadMore],
  );

  React.useEffect(() => {
    const debouncedHandler = debounce(handleMovement, 500);
    window.addEventListener('resize', debouncedHandler);
    return () => {
      window.removeEventListener('resize', debouncedHandler);
    };
  }, [handleMovement]);

  // NOTE (kyle): fire off once the component has loaded
  React.useEffect(() => handleMovement(), []);

  return [containerRef, loaderRef, handleMovement];
}
